/**
 * Homepage marketing widget content template (artifact + client paths).
 *
 * Markup is intentionally duplicated in CMT for asset authoring preview — not shared via packages.
 * Keep visual parity with:
 * apps/admin/ui/src/app/pages/CampaignManagement/assets/components/SideCardPreviewTemplate.tsx
 * (including optional footer — `SideCardPreviewFooter` / `MarketingWidgetFooter`)
 * Changes to widget layout or styling here must be reflected in the CMT preview, and vice versa.
 */
import { ReactNode } from 'react';
import { useEffectOnce } from 'react-use';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { resolveMarketingMessageFooterLogo } from './marketingMessageFooterLogoRegistry';

export type ClientMarketingWidgetType =
  | 'how_it_works'
  | 'quarter_end_opportunity'
  | 'fed_rate_cut'
  | 'lending_connections'
  | 'financeiq';

export type MarketingWidgetTrackingMetadata = {
  location: 'homepage';
  source: 'artifact' | 'client';
  widgetType?: ClientMarketingWidgetType;
  ctaRole?: 'primary' | 'secondary';
  artifactUuid?: string;
  assetUuid?: string;
  campaignUuid?: string;
  campaignRunUuid?: string;
} & Record<string, unknown>;

export interface MarketingWidgetTemplateProps {
  /**
   * Icon in the blue corner — Liquidity SVG via `renderLiquidityIconComponent`:
   * - **Client widget** (`useClientMarketingWidget`): hard-coded marketing icons
   * - **Campaign artifact** (`mapMarketingArtifactToWidgetProps`): `content.icon.key` via `renderArtifactIcon`
   */
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  buttonLink?: string;
  buttonText?: string;
  isExternalLink?: boolean;
  secondaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryIsExternalLink?: boolean;
  footer?: {
    text: string;
    logoIconKey?: string;
    logoAlt: string;
  };
  trackingMetadata: MarketingWidgetTrackingMetadata;
}

interface MarketingWidgetFooterProps {
  text: string;
  logoIconKey?: string;
  logoAlt: string;
}

/** Enterprise ignores `footer.logo.url` from artifacts — registry icon keys only. */
const MarketingWidgetFooter = ({
  text,
  logoIconKey,
  logoAlt,
}: MarketingWidgetFooterProps): React.ReactElement | null => {
  const resolvedText = text.trim();
  const ResolvedFooterLogo = resolveMarketingMessageFooterLogo(logoIconKey?.trim() || undefined);

  if (!resolvedText && !ResolvedFooterLogo) {
    return null;
  }

  // Lead-in text (e.g. "Powered by") + one logo graphic.
  return (
    <div
      data-testid="marketing-widget-footer"
      className="mt-auto flex min-h-12 items-center gap-2 bg-blue-700 px-6 py-3 text-sm text-white"
    >
      {resolvedText ? <span>{resolvedText}</span> : null}
      {ResolvedFooterLogo ? (
        <ResolvedFooterLogo role="img" aria-label={logoAlt.trim() || 'Footer logo'} className="h-5 w-auto text-white" />
      ) : null}
    </div>
  );
};

interface MarketingWidgetCtaButtonProps {
  buttonLink: string;
  buttonText: string;
  isExternalLink: boolean;
  variant: 'secondary' | 'ancillary';
  onClick: () => void;
}

const MarketingWidgetCtaButton = ({
  buttonLink,
  buttonText,
  isExternalLink,
  variant,
  onClick,
}: MarketingWidgetCtaButtonProps) => {
  if (isExternalLink) {
    return (
      <ButtonLink
        to={buttonLink}
        className="sm:w-fit"
        rel="noopener noreferrer"
        variant={variant}
        target="_blank"
        onClick={onClick}
      >
        {buttonText}
      </ButtonLink>
    );
  }

  return (
    <ButtonLink to={asEnterpriseLink(buttonLink)} className="sm:w-fit" variant={variant} onClick={onClick}>
      {buttonText}
    </ButtonLink>
  );
};

const MarketingWidgetTemplate = ({
  icon,
  title,
  description,
  buttonLink,
  buttonText,
  isExternalLink = false,
  secondaryButtonLink,
  secondaryButtonText,
  secondaryIsExternalLink = false,
  footer,
  trackingMetadata,
}: MarketingWidgetTemplateProps) => {
  const { trackEnterpriseEvent } = useReporting();

  useEffectOnce(() => {
    trackEnterpriseEvent('widget::shown', trackingMetadata);
  });

  const handlePrimaryClick = () => {
    trackEnterpriseEvent('widget::clicked', { ...trackingMetadata, ctaRole: 'primary' });
  };

  const handleSecondaryClick = () => {
    trackEnterpriseEvent('widget::clicked', { ...trackingMetadata, ctaRole: 'secondary' });
  };

  const hasPrimaryCta = Boolean(buttonLink && buttonText);
  const hasSecondaryCta = Boolean(secondaryButtonLink && secondaryButtonText);

  return (
    <>
      <div className="w-fit rounded-br-2xl bg-blue-600">{icon}</div>
      <div className="space-y-6 px-6 py-10">
        <div className="space-y-4">
          <h4 className="m-0 font-serif text-2xl font-bold">{title}</h4>
          <div>{description}</div>
        </div>
        {(hasPrimaryCta || hasSecondaryCta) && (
          <div className="flex flex-wrap items-center gap-3">
            {hasPrimaryCta && buttonLink && buttonText && (
              <MarketingWidgetCtaButton
                buttonLink={buttonLink}
                buttonText={buttonText}
                isExternalLink={isExternalLink}
                variant="secondary"
                onClick={handlePrimaryClick}
              />
            )}
            {hasSecondaryCta && secondaryButtonLink && secondaryButtonText && (
              <MarketingWidgetCtaButton
                buttonLink={secondaryButtonLink}
                buttonText={secondaryButtonText}
                isExternalLink={secondaryIsExternalLink}
                variant="ancillary"
                onClick={handleSecondaryClick}
              />
            )}
          </div>
        )}
      </div>
      {footer ? (
        <MarketingWidgetFooter text={footer.text} logoIconKey={footer.logoIconKey} logoAlt={footer.logoAlt} />
      ) : null}
    </>
  );
};

export default MarketingWidgetTemplate;
